/**
 * Tiny imperative toast system. Components subscribe in their lifecycle and
 * render the active list; the rest of the app just calls `toast.success(...)`
 * etc. No context provider needed.
 */

export type ToastKind = "success" | "error" | "info" | "warning";

export type Toast = {
  id: number;
  kind: ToastKind;
  text: string;
  /** Auto-dismiss after this many ms. 0 = sticky until user dismisses. */
  duration: number;
};

type Listener = (toasts: Toast[]) => void;

let nextId = 1;
let toasts: Toast[] = [];
const listeners = new Set<Listener>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  // Snapshot so React state setters get a new reference.
  const snapshot = toasts.slice();
  for (const l of listeners) l(snapshot);
}

function push(kind: ToastKind, text: string, duration: number): number {
  const id = nextId++;
  toasts = [...toasts, { id, kind, text, duration }];
  emit();
  if (duration > 0) {
    const timer = setTimeout(() => dismiss(id), duration);
    timers.set(id, timer);
  }
  return id;
}

export function dismiss(id: number): void {
  toasts = toasts.filter((t) => t.id !== id);
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  emit();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(toasts.slice());
  return () => {
    listeners.delete(listener);
  };
}

export const toast = {
  success: (text: string, duration = 3000) => push("success", text, duration),
  error:   (text: string, duration = 5000) => push("error", text, duration),
  info:    (text: string, duration = 3000) => push("info", text, duration),
  warning: (text: string, duration = 4000) => push("warning", text, duration),
};
